import { ReactNode } from "react";
import { Stack, Text, useMantineTheme } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { showSuccessNotification } from "@utils/notifications";
import ErrorMessage from "@common/components/text/ErrorMessage";
import IconDatabase from "@common/components/icons/IconDatabase";
import { ADDON_PRODUCT_DWH_RENT_TYPE } from "@stores/addons/constants";
import useChangePlanWithInstanceRestart from "@hooks/useChangePlanWithInstanceRestart";
import { useAddOnContext } from "@addons/hooks";
import { hasProductAnalytics } from "@addons/utils";
import SelfHostedWarning from "@components/forms/storage/SelfHostedWarning";
import StorageRentUsageGraph from "@addons/dwh-rent/UsageGraph";
import AddStorageModal from "@components/forms/storage/AddStorageModal";
import { formatPrice, formatQuantity } from "@common/utils/string";
import RemoveStorageRentModal from "@components/forms/storage/RemoveStorageRentModal";
import { AddOnCard } from "@addons/components/AddOnCard";
import { NUMBER_FORMAT_PRICE_OPTIONS } from "@common/utils/string/constants";

export interface IStorageRentActionProps {
  subscribed: boolean;
  loading: boolean;
  disabled: boolean;
  onClick(): void;
}

interface Props {
  /** the enable/disable control — the caller decides link or button */
  action(props: IStorageRentActionProps): ReactNode;
}

export default function ManageStorageRentForm({ action }: Props) {
  const theme = useMantineTheme();

  const addOnContext = useAddOnContext("dwh-rent");
  const {
    purchase,
    subscribed,
    isCloudHosted,
    isYearly,
    canChangeSubscription,
  } = addOnContext;

  const usage = addOnContext.getUsage();
  const pricePer1M = 1_000_000 * addOnContext.getPricePerUnit();
  const includedRows = formatQuantity(addOnContext.getIncludedUnits());
  const hasStorage = !!subscribed;
  const billingFrequencyText = isYearly ? "year" : "month";

  const [
    addStorageModalOpened,
    { open: openAddStorageModal, close: closeAddStorageModal },
  ] = useDisclosure(false);
  const [
    removeStorageModalOpened,
    { open: openRemoveStorageModal, close: closeRemoveStorageModal },
  ] = useDisclosure(false);

  const {
    isPending: changingPlan,
    isUpdatingSubscription,
    error: changePlanError,
    mutate: changePlan,
  } = useChangePlanWithInstanceRestart(purchase);

  const notifyStorageChange = (message: string) =>
    showSuccessNotification({ icon: <IconDatabase />, message });

  const enableStorage = () =>
    changePlan(addOnContext.getEnableAddOnPayload(), {
      onSuccess: () => notifyStorageChange("Storage enabled."),
    });

  const disableStorage = () =>
    changePlan(addOnContext.getDisableAddOnPayload(), {
      onSuccess: () =>
        notifyStorageChange("Storage disabled and all data deleted."),
    });

  return (
    <>
      <Stack data-testid="ManageStorageRentForm" spacing="md">
        {!isCloudHosted && (
          <SelfHostedWarning dataTestId="ManageStorageRentForm-warning_self-hosted" />
        )}

        {isCloudHosted && (
          <AddOnCard
            isYearly={isYearly}
            productType={ADDON_PRODUCT_DWH_RENT_TYPE}
            title="Storage"
            disabled={!canChangeSubscription}
            extra={action({
              subscribed: hasStorage,
              loading: isUpdatingSubscription,
              disabled: changingPlan,
              onClick: hasStorage
                ? openRemoveStorageModal
                : openAddStorageModal,
            })}
            description={
              <>
                Secure place to store your spreadsheets for exploration.
                {!hasStorage && (
                  <Text c="gray.4" size="sm" maw={660} mt="xs">
                    {includedRows} of stored rows included for free. Price per
                    row afterwards:{" "}
                    {formatPrice(pricePer1M, NUMBER_FORMAT_PRICE_OPTIONS)} per
                    1M of stored rows.
                  </Text>
                )}
              </>
            }
            usage={
              (hasStorage || changePlanError) && (
                <>
                  {hasStorage && <StorageRentUsageGraph addOn={addOnContext} />}
                  <ErrorMessage mt={theme.spacing.xs} error={changePlanError} />
                </>
              )
            }
          />
        )}
      </Stack>

      <AddStorageModal
        title={`${includedRows} stored rows included for free.`}
        description={`After the first ${includedRows} rows, you'll be charged ${formatPrice(pricePer1M)}/${billingFrequencyText} for every additional 1M of stored rows. You can cancel and disable storage anytime.`}
        opened={addStorageModalOpened}
        onClose={(save) => {
          if (save) {
            enableStorage();
          }

          closeAddStorageModal();
        }}
      />

      <RemoveStorageRentModal
        rowsToErase={usage?.hasQuota ? usage.usage : undefined}
        hasProductAnalytics={hasProductAnalytics(addOnContext)}
        opened={removeStorageModalOpened}
        onClose={(save) => {
          if (save) {
            disableStorage();
          }

          closeRemoveStorageModal();
        }}
      />
    </>
  );
}
